import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Fuse from 'fuse.js';
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Combobox } from "../components/ui/combobox";
import { Search, Plus, Copy, Trash2, TestTube, ClipboardList } from 'lucide-react';
import FilterModal from '@/Modal/FilterModal';
import NewIncidentModal from '@/Modal/NewIncidentModal';
import DuplicateChecker from '@/components/DuplicateChecker';
import { CommanServices } from '@/services/commanServices';


const ActionCards = () => {
  const [actionCards, setActionCards] = useState([]);
  const [filteredCards, setFilteredCards] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCard, setSelectedCard] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [duplicateValidation, setDuplicateValidation] = useState({
    isChecking: false,
    isDuplicate: false,
    message: '',
    isValid: true
  });
  const [rightPanelWidth, setRightPanelWidth] = useState(() => Math.floor(window.innerWidth * 0.35)); // Default ~35% of screen width
  
  // Fuse.js configuration for fuzzy search
  const fuseOptions = {
    keys: [
      'card_name',
      'description'
    ],
    threshold: 0.3,
    ignoreLocation: true,
    findAllMatches: true
  };
  
  const fuse = useMemo(() => new Fuse(actionCards, fuseOptions), [actionCards]);
  
  // Mouse drag handler for resizable panels
  const handleMouseDown = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = rightPanelWidth;

    const handleMouseMove = (e) => {
      const deltaX = startX - e.clientX;
      const newWidth = Math.max(300, Math.min(800, startWidth + deltaX));
      setRightPanelWidth(newWidth);
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const [formData, setFormData] = useState({
    card_name: "",
    description: "",
    action_tasks: []
    // Removed: incident_conditions, is_active, auto_trigger, eligible_departments per requirements
  });
  
  const [suggestions, setSuggestions] = useState({
    incidentTypes: [],
    events: [],
    locations: [],
    incidentRulesData: [],
    incidentRelationships: {}
  });

  // Widget management state
  const [availableWidgets, setAvailableWidgets] = useState([
    { id: 'action-cards-summary', name: 'Action Cards Summary', description: 'Overview of action card statistics', enabled: true },
    { id: 'recent-triggers', name: 'Recent Triggers', description: 'Recently triggered action cards', enabled: true },
    { id: 'performance-metrics', name: 'Performance Metrics', description: 'Action card performance analytics', enabled: false }
  ]);
  const [showWidgetModal, setShowWidgetModal] = useState(false);

  // Filter Modal state and data
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showNewIncidentModal, setShowNewIncidentModal] = useState(false);
  const [incidents, setIncidents] = useState([]);
  const [incidentClassifications, setIncidentClassifications] = useState([]);
  const [filters, setFilters] = useState({
    location: [],
    level: [],
    status: [],
    dateType: 'all',
    startDate: '',
    endDate: '',
    incidentType: [],
    event: [],
    trigger: [],
    incidentFamily: '' // Filter by incident family tree
  });

  useEffect(() => {
    fetchActionCards();
    fetchSuggestions();
  }, []);

  // Fuzzy search filtering using Fuse.js
  useEffect(() => {
    try {
      if (!searchTerm.trim()) {
        setFilteredCards(actionCards);
        return;
      }
      
      const results = fuse.search(searchTerm);
      const filtered = results.map(result => result.item);
      setFilteredCards(filtered);
    } catch (error) {
      console.error('Error filtering action cards:', error);
      setFilteredCards(actionCards);
    }
  }, [actionCards, searchTerm, fuse]);

  const fetchActionCards = async () => {
    try {
      const response = await CommanServices.getactioncardsData();
      if (response.status) {
        setActionCards(response.data);
      }
    } catch (error) {
      console.error("Error fetching action cards:", error);
    }
  };

  const fetchSuggestions = async () => {
    try {
      const response = await CommanServices.deparincidentclassificationsrules();
      if (response.status) {
        setSuggestions({
          incidentTypes: response.data.unique_types,
          events: response.data.unique_events,
          locations: response.data.unique_locations,
          incidentRulesData: response.data.incidents,
          incidentRelationships: response.data.relationships
        });
      }
    } catch (error) {
      console.error("Error fetching suggestions:", error);
    }
  };

  const handleSave = async () => {
    try {
      setIsUpdating(true);
      
      if (isEditing && selectedCard) {
        const response = await CommanServices.updateactioncards(selectedCard.id, formData);
        if (response.status) {
          // Update the form with the saved data to show persistence, don't reset to defaults
          setFormData(response.data);
          setSelectedCard(response.data);
        }
      } else {
        const response = await CommanServices.addactioncards(formData);
        
        // For new cards, set the created card as selected to show the saved state
        if (response.status) {
          setFormData(response.data);
          setSelectedCard(response.data);
          setIsEditing(true); // Switch to edit mode to show saved state
        }else{
          alert(response.message)
        }
      }
      
      // Refresh the action cards list to show the updated card
      fetchActionCards();
      
      // SUCCESS: Don't reset form - show the saved data instead
      alert("Action Card saved successfully!");
      
    } catch (error) {
      console.error("Error saving action card:", error);
      alert("Error saving action card. Please try again.");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await CommanServices.deleteactioncards(id);
      fetchActionCards();
      if (selectedCard && selectedCard.id === id) {
        resetForm();
      }
    } catch (error) {
      console.error("Error deleting action card:", error);
    }
  };

  const handleCopy = (card) => {
    // Create a copy of the card with a new name and reset state
    const copiedCard = {
      ...card,
      card_name: `${card.card_name} (Copy)`,
      id: undefined // Remove ID so it creates a new card
    };
    
    setFormData(copiedCard);
    setSelectedCard(null);
    setIsEditing(false);
  };

  // Handle action card name creation for combobox
  const handleActionCardNameCreate = async (newName) => {
    if (!newName || !newName.trim()) return;
    
    // Update form data immediately for better UX
    setFormData({...formData, card_name: newName});
  };

  const handleTrigger = async (id) => {
    try {
      const response = await CommanServices.postactioncardstrigger(id);
      alert(`Action Card Triggered!\n\n${response.message}\n\nTriggered at: ${new Date(response.data.triggered_at).toLocaleString()}`);
    } catch (error) {
      console.error("Error triggering action card:", error);
      alert("Error triggering action card. Please try again.");
    }
  };

  const testWildcardLogic = () => {
    if (!formData.incident_conditions.some(c => c.incident_type)) {
      alert("Please add at least one incident condition with an incident type to test wildcard logic.");
      return;
    }

    let testResults = [];
    
    formData.incident_conditions.forEach((condition, index) => {
      const wildcards = [];
      if (!condition.event) wildcards.push("All Events");
      if (!condition.alert_trigger_value) wildcards.push("All Trigger Values");
      if (!condition.location_zone) {
        wildcards.push("All Locations");
      } else if (condition.location_zone?.location?.wildcards?.length > 0) {
        wildcards.push("Location Wildcards");
      }
      
      const wildcardText = wildcards.length > 0 ? wildcards.join(", ") : "No wildcards (specific match)";
      
      testResults.push(`Condition ${index + 1}:
- Incident Type: ${condition.incident_type}
- Event: ${condition.event || "🌟 WILDCARD - All Events"}
- Trigger Value: ${condition.alert_trigger_value || "🌟 WILDCARD - All Trigger Values"}
- Location: ${condition.location_zone ? (condition.location_zone.display_label || condition.location_zone.root_label || "Selected Location") : "🌟 WILDCARD - All Locations"}
- Active Wildcards: ${wildcardText}`);
    });

    alert(`🧪 Wildcard Logic Test Results:\n\n${testResults.join('\n\n')}\n\n✨ This action card will trigger for any incident matching these conditions, with wildcards applying to ALL possible values for empty fields.`);
  };

  const resetForm = () => {
    setFormData({
      card_name: "",
      description: "",
      action_tasks: []
      // Removed: incident_conditions, is_active, auto_trigger, eligible_departments per requirements
    });
    setSelectedCard(null);
    setIsEditing(false);
  };

  const editCard = (card) => {
    // Ensure location_zone fields are arrays for compatibility with LocationSelector
    const normalizedCard = {
      ...card,
      incident_conditions: card.incident_conditions.map(condition => ({
        ...condition,
        location_zone: Array.isArray(condition.location_zone) 
          ? condition.location_zone 
          : condition.location_zone 
            ? [condition.location_zone] 
            : []
      }))
    };
    setFormData(normalizedCard);
    setSelectedCard(card);
    setIsEditing(true);
  };

  // Condition management
  const addCondition = () => {
    setFormData({
      ...formData,
      incident_conditions: [
        ...formData.incident_conditions,
        {
          incident_type: "",
          event: "",
          alert_trigger_value: "",
          location_zone: [] // Initialize as empty array for LocationSelector
        }
      ]
    });
  };

  const removeCondition = (index) => {
    const newConditions = formData.incident_conditions.filter((_, i) => i !== index);
    setFormData({ ...formData, incident_conditions: newConditions });
  };

  const updateCondition = (index, field, value) => {
    const newConditions = formData.incident_conditions.map((condition, i) => {
      if (i === index) {
        const updatedCondition = { ...condition, [field]: value };
        
        // Reset dependent fields when parent selection changes
        if (field === 'incident_type') {
          updatedCondition.event = '';
          updatedCondition.alert_trigger_value = '';
        } else if (field === 'event') {
          updatedCondition.alert_trigger_value = '';
        }
        
        return updatedCondition;
      }
      return condition;
    });
    setFormData({ ...formData, incident_conditions: newConditions });
  };

  // Task management
  const addTask = () => {
    setFormData({
      ...formData,
      action_tasks: [
        ...formData.action_tasks,
        {
          id: `task_${Date.now()}`,
          task_text: "",
          instructions: "",
          order_index: formData.action_tasks.length,
          is_completed: false
        }
      ]
    });
  };

  const removeTask = (index) => {
    const newTasks = formData.action_tasks.filter((_, i) => i !== index);
    // Re-index remaining tasks
    const reindexedTasks = newTasks.map((task, i) => ({ ...task, order_index: i }));
    setFormData({ ...formData, action_tasks: reindexedTasks });
  };

  const updateTask = (index, field, value) => {
    const newTasks = formData.action_tasks.map((task, i) =>
      i === index ? { ...task, [field]: value } : task
    );
    setFormData({ ...formData, action_tasks: newTasks });
  };

  // Helper functions for smart filtering
  const getFilteredEvents = (incidentType) => {
    if (!incidentType || !suggestions.incidentRelationships[incidentType]) {
      return [];
    }
    return Object.keys(suggestions.incidentRelationships[incidentType]).sort();
  };

  const getFilteredTriggerValues = (incidentType, event) => {
    if (!incidentType || !event || !suggestions.incidentRelationships[incidentType]?.[event]) {
      return [];
    }
    return suggestions.incidentRelationships[incidentType][event].sort();
  };

  // Location filtering is now handled by LocationSelector component

  const isFormValid = () => {
    const basicValidation = formData.card_name.trim() && 
                           formData.action_tasks.length > 0 &&
                           formData.action_tasks.every(task => task.task_text.trim());
    
    const noDuplicateError = !duplicateValidation.isDuplicate && !duplicateValidation.isChecking && duplicateValidation.isValid;
    
    return basicValidation && noDuplicateError;
  };

  // Widget Management Functions
  const toggleWidget = (widgetId) => {
    setAvailableWidgets(widgets => 
      widgets.map(widget => 
        widget.id === widgetId 
          ? { ...widget, enabled: !widget.enabled }
          : widget
      )
    );
  };

  const enabledWidgets = availableWidgets.filter(widget => widget.enabled);

  // Filter handling functions
  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleResetFilters = () => {
    setFilters({
      location: [],
      level: [],
      status: [],
      dateType: 'all',
      startDate: '',
      endDate: '',
      incidentType: [],
      event: [],
      trigger: [],
      incidentFamily: ''
    });
  };

  const handleApplyFilters = () => {
    setShowFilterModal(false);
  };

  // Handle new incident creation
  const handleIncidentCreated = (newIncident) => {
    console.log('New incident created:', newIncident);
    // Refresh action cards data
    fetchActionCards();
  };

  // Widget Modal Component
  const WidgetModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowWidgetModal(false)}>
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Manage Dashboard Widgets</h3>
          <Button variant="ghost" size="sm" onClick={() => setShowWidgetModal(false)}>
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="space-y-3">
          {availableWidgets.map((widget) => (
            <div key={widget.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
              <div className="flex-1">
                <h4 className="text-sm font-medium text-gray-900">{widget.name}</h4>
                <p className="text-xs text-gray-500 mt-1">{widget.description}</p>
              </div>
              <Button
                size="sm"
                variant={widget.enabled ? "default" : "outline"}
                onClick={() => toggleWidget(widget.id)}
                className={`ml-3 ${widget.enabled ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
              >
                {widget.enabled ? (
                  <>
                    <Eye className="h-3 w-3 mr-1" />
                    Remove
                  </>
                ) : (
                  <>
                    <Plus className="h-3 w-3 mr-1" />
                    Add
                  </>
                )}
              </Button>
            </div>
          ))}
        </div>
        <div className="flex justify-end mt-6">
          <Button onClick={() => setShowWidgetModal(false)}>
            Done
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {showWidgetModal && <WidgetModal />}
      {showFilterModal && (
        <FilterModal
          isOpen={showFilterModal}
          onClose={() => setShowFilterModal(false)}
          incidents={incidents}
          incidentClassifications={incidentClassifications}
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onReset={handleResetFilters}
          onApply={handleApplyFilters}
        />
      )}

      {showNewIncidentModal && (
        <NewIncidentModal
          isOpen={showNewIncidentModal}
          onClose={() => setShowNewIncidentModal(false)}
          onIncidentCreated={handleIncidentCreated}
          incidentClassifications={incidentClassifications}
        />
      )}
      <div className="h-screen relative bg-gray-50 flex overflow-hidden">

      {/* Main Content */}
      <div className="flex-1 flex">
        {/* Left Panel - Action Cards List */}
        <div className="flex-1 p-6" style={{ marginRight: `${rightPanelWidth + 4}px` }}>
          <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Action Cards</h2>
          <p className="text-sm text-gray-600 mb-4">Create automated response checklists triggered by specific incident conditions. Click on any action card to view and edit details.</p>
          
          {/* Quick Search Bar - consistent with other modules */}
          <div className="flex items-center justify-between gap-4 mb-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search action cards..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button onClick={resetForm} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />
              Add Action Card
            </Button>
          </div>
        </div>

        <div className="space-y-4 max-h-[calc(100vh-250px)] overflow-y-auto pb-6">
          {filteredCards.map((card) => (
            <Card 
              key={card.id} 
              className={`cursor-pointer hover:shadow-md transition-all duration-200 border-2 ${
                selectedCard?.id === card.id 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => editCard(card)}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-800">{card.card_name}</h3>
                    <p className="text-sm text-gray-600 mt-1">{card.description}</p>
                    <div className="flex items-center gap-2 mt-2">
                      {/* Active/Auto-trigger chips removed per requirements */}
                      <Badge className="bg-green-100 text-green-800">
                        {card.action_tasks.length} tasks
                      </Badge>
                      {/* Wildcard chips removed per requirements */}
                    </div>
                  </div>
                  <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                    {/* Test and Edit buttons removed per requirements */}
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleCopy(card)}
                      className="bg-blue-50 hover:bg-blue-100"
                    >
                      <Copy className="w-3 h-3 mr-1" />
                      Copy
                    </Button>
                    <Button 
                      variant="destructive" 
                      size="sm" 
                      onClick={() => handleDelete(card.id)}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </CardContent>
          </Card>
          ))}
        </div>
      </div>
      </div>

      {/* Resize Handle */}
      <div 
        className="w-1 bg-gray-300 hover:bg-blue-400 cursor-col-resize transition-colors flex-shrink-0 relative group"
        onMouseDown={handleMouseDown}
        style={{ position: 'absolute', right: `${rightPanelWidth}px`, top: 0, bottom: 0, zIndex: 10 }}
      >
        <div className="absolute inset-y-0 -left-1 -right-1 group-hover:bg-blue-400 group-hover:opacity-50"></div>
      </div>

      {/* Right Panel - Action Card Editor */}
      <div 
        className="bg-white shadow-lg p-6 overflow-y-auto max-h-[calc(100vh-81px)] border-l border-gray-200 absolute right-0 top-0 bottom-0"
        style={{ width: `${rightPanelWidth}px` }}
      >
        <h3 className="text-lg font-semibold mb-4">
          {isEditing ? "Edit Action Card" : "New Action Card"}
        </h3>

        <div className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h4 className="font-medium text-gray-800 border-b pb-2">Basic Information</h4>
            
            <div>
              <label className="block text-sm font-medium mb-1">
                Action Card Name <span className="text-red-500">*</span>
              </label>
              <Combobox
                value={formData.card_name}
                onValueChange={(value) => setFormData({...formData, card_name: value})}
                onCreate={handleActionCardNameCreate}
                placeholder="Type to search or create action card name..."
                options={actionCards.map(card => ({ value: card.card_name, label: card.card_name }))}
                createLabel="Create"
                className={`${
                  !formData.card_name?.trim() 
                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                    : 'border-gray-300'
                }`}
              />
              <DuplicateChecker
                module="action-cards"
                value={formData.card_name}
                excludeId={selectedCard?.id}
                onValidationChange={setDuplicateValidation}
                disabled={isUpdating}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Description</label>
              <Textarea
                placeholder="Brief description of when this action card should be triggered..."
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows={2}
              />
            </div>

            {/* Active and Auto-trigger toggles removed per requirements */}
          </div>

          {/* Linked Incident Conditions section removed per requirements */}

          {/* Action Tasks */}
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b pb-2">
              <h4 className="font-medium text-gray-800">Action Card Tasks</h4>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addTask}
              >
                <Plus className="w-3 h-3 mr-1" />
                Add Task
              </Button>
            </div>
            
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {formData.action_tasks.map((task, index) => (
                <div key={task.id} className="border rounded-lg p-3 bg-blue-50">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium">Task {index + 1}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeTask(index)}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                  
                  <Textarea
                    placeholder="e.g., Check shelter capacity and ensure adequate supplies for displaced patients"
                    value={task.task_text}
                    onChange={(e) => updateTask(index, 'task_text', e.target.value)}
                    rows={2}
                    className="text-sm"
                  />

                  <div className="mt-2">
                    <label className="block text-[11px] font-semibold text-gray-500 mb-1 flex items-center gap-1">
                      <ClipboardList className="w-3 h-3 text-indigo-500" /> Instructions to perform
                    </label>
                    <Textarea
                      placeholder="Step-by-step instructions the responder must follow for this task…"
                      value={task.instructions || ''}
                      onChange={(e) => updateTask(index, 'instructions', e.target.value)}
                      rows={2}
                      className="text-sm bg-white"
                      data-testid={`task-instructions-${index}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Validation Messages */}
          {(!isFormValid() || duplicateValidation.isDuplicate) && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700 font-medium">
                ⚠️ {duplicateValidation.isDuplicate ? duplicateValidation.message : 'Please complete all required fields'}
              </p>
              {!duplicateValidation.isDuplicate && (
                <div className="text-xs text-red-600 mt-1 space-y-1">
                  {!formData.card_name.trim() && <p>• Action Card Name is required</p>}
                  {formData.action_tasks.length === 0 && <p>• At least one action task is required</p>}
                  {formData.action_tasks.some(task => !task.task_text.trim()) && <p>• All task descriptions must be completed</p>}
                </div>
              )}
            </div>
          )}

          {/* Action Buttons - Test Wildcard removed per requirements */}
          <div className="flex gap-2 pt-4">
            <Button variant="outline" onClick={resetForm} className="flex-1">
              Cancel
            </Button>
            <Button 
              onClick={() => {
                console.log("🔥 BUTTON CLICKED! Testing event handler...");
                handleSave();
              }}
              disabled={!isFormValid() || isUpdating}
              className={`flex-1 ${
                isFormValid() && !isUpdating
                  ? 'bg-blue-600 hover:bg-blue-700' 
                  : 'bg-gray-400 cursor-not-allowed opacity-60'
              }`}
            >
              {isUpdating ? 'Updating...' : (isEditing ? "Update" : "Save")}
            </Button>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default ActionCards;